import { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { useLocation } from '@react-router';
import type { TabNavItem, UseTabNavReturn } from './TabNav.types';
import { KEYBOARD_KEYS } from '@shared/joykit/packages/core/common/utils/keyboard';
import { withWindow } from '@shared/utils/withWindow';

/**
 * Safely retrieves the current pathname from React Router.
 * Returns '' when no Router provider is present, avoiding crashes
 * in non-router contexts (Storybook, isolated tests, standalone usage).
 */
function useSafePathname(): string {
  try {
    const location = useLocation();
    return location?.pathname ?? '';
  } catch {
    return '';
  }
}

type UseTabNavOptions = Readonly<{
  items: ReadonlyArray<TabNavItem>;
  variant?: 'default' | 'native';
  indicatorStyle?: 'underline' | 'pill';
  onTabChange?: (item: TabNavItem, index: number) => void;
}>;

/**
 * Determines the active tab index based on items configuration and current URL.
 * If an item has `isActive: true`, it takes precedence.
 * Otherwise, we match based on the current pathname.
 * Returns -1 when no tab matches.
 */
function getActiveIndex(items: ReadonlyArray<TabNavItem>, pathname: string): number {
  const explicitActiveIndex = items.findIndex(item => item.isActive === true);
  if (explicitActiveIndex !== -1) {
    return explicitActiveIndex;
  }

  const exactMatchIndex = items.findIndex(item => pathname === item.href);
  if (exactMatchIndex !== -1) {
    return exactMatchIndex;
  }

  // Prefix match: find the longest matching prefix to avoid ambiguity
  // (e.g. `/photos/album` should match `/photos` over `/photo`)
  const prefixMatches = items.map((item, i) => ({ index: i, href: item.href })).filter(({ href }) => href !== '/' && pathname.startsWith(href));

  if (prefixMatches.length > 0) {
    return prefixMatches.reduce((best, curr) => (curr.href.length > best.href.length ? curr : best)).index;
  }

  return -1;
}

/**
 * Hook for managing TabNav state, keyboard navigation, and sliding indicator.
 */
export function useTabNav({ items, variant = 'default', indicatorStyle = 'underline', onTabChange }: UseTabNavOptions): UseTabNavReturn {
  const isNative = variant === 'native';
  const isPillStyle = indicatorStyle === 'pill';
  const hasSlidingPillIndicator = isNative || isPillStyle;
  const pathname = useSafePathname();

  // activeIndex is the single source of truth — derived directly from items + URL.
  // No state sync needed; useMemo recomputes when inputs change.
  const activeIndex = useMemo(() => getActiveIndex(items, pathname), [items, pathname]);

  // Menu open/close state is the only stateful concern.
  const [menuOpenState, setMenuOpenState] = useState<Record<number, boolean>>({});

  // Refs for indicator positioning
  const indicatorRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const listRef = useRef<HTMLUListElement>(null);
  const hasInitializedIndicator = useRef(false);

  /**
   * Update indicator position and width based on active tab.
   *
   * On the very first *meaningful* positioning (non-zero width), CSS transitions
   * are disabled so the indicator doesn't animate from position 0. A forced reflow
   * ensures the browser applies the no-transition position synchronously, then
   * transitions are re-enabled for future tab changes (which will slide smoothly).
   */
  const updateIndicator = useCallback(() => {
    const indicator = indicatorRef.current;
    const list = listRef.current;

    if (!indicator || !list) return;

    // When no tab is active, hide the indicator
    if (activeIndex === -1) {
      indicator.style.opacity = '0';
      return;
    }

    const activeItem = itemRefs.current[activeIndex];
    if (!activeItem) return;

    const listRect = list.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();

    const left = itemRect.left - listRect.left;
    const width = itemRect.width;

    // Skip positioning if the element has no dimensions yet (e.g., parent is
    // display: none, CSS not yet applied, or font not loaded). The ResizeObserver
    // will call us again once the element has real dimensions.
    if (width === 0) return;

    // Only consider the indicator "initialized" once it has been positioned with
    // real dimensions. This ensures the first visible positioning always happens
    // without a transition — even if a prior call was skipped due to zero width.
    const isFirstPositioning = !hasInitializedIndicator.current;

    if (isFirstPositioning) {
      indicator.style.transition = 'none';
      hasInitializedIndicator.current = true;
    }

    indicator.style.opacity = '1';

    // Pill-style indicators (native and pill) need translateY(-50%) for vertical centering
    indicator.style.transform = hasSlidingPillIndicator ? `translateX(${left}px) translateY(-50%)` : `translateX(${left}px)`;
    indicator.style.width = `${width}px`;

    if (isFirstPositioning) {
      // Force a synchronous reflow so the browser applies the position change
      // with transition: none. Then re-enable transitions for future tab changes.
      // This all happens within useLayoutEffect (before paint), so the user
      // never sees the indicator at position 0.
      void indicator.offsetHeight;
      indicator.style.transition = '';
    }
  }, [activeIndex, hasSlidingPillIndicator]);

  // Position indicator before paint on mount and when activeIndex changes.
  // React guarantees ref callbacks fire before useLayoutEffect during the
  // commit phase, so itemRefs are always populated when this runs.
  useLayoutEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  // Reposition indicator when list layout changes (e.g., CSS applied after mount,
  // font loading, responsive display toggling from none → flex). This catches cases
  // where useLayoutEffect runs before the browser has applied styles, leaving the
  // indicator at width 0.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const ro = new ResizeObserver(() => {
      updateIndicator();
    });
    ro.observe(list);

    return () => ro.disconnect();
  }, [updateIndicator]);

  // Update indicator on window resize
  useEffect(() => {
    withWindow(w => {
      w.addEventListener('resize', updateIndicator);
    });
    return () => {
      withWindow(w => {
        w.removeEventListener('resize', updateIndicator);
      });
    };
  }, [updateIndicator]);

  /**
   * Handle keyboard navigation (arrow keys, Home, End).
   *
   * This is a progressive enhancement — all links remain tabbable via Tab.
   * Arrow keys provide quick navigation between siblings within the nav.
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, currentIndex: number) => {
      const { key } = e;
      const lastIndex = items.length - 1;

      let nextIndex = currentIndex;

      switch (key) {
        case KEYBOARD_KEYS.arrowRight:
          nextIndex = currentIndex < lastIndex ? currentIndex + 1 : 0;
          e.preventDefault();
          break;
        case KEYBOARD_KEYS.arrowLeft:
          nextIndex = currentIndex > 0 ? currentIndex - 1 : lastIndex;
          e.preventDefault();
          break;
        case KEYBOARD_KEYS.home:
          nextIndex = 0;
          e.preventDefault();
          break;
        case KEYBOARD_KEYS.end:
          nextIndex = lastIndex;
          e.preventDefault();
          break;
        default:
          return;
      }

      const nextElement = itemRefs.current[nextIndex];
      if (nextElement) {
        const focusable = nextElement.querySelector('a, button') as HTMLElement;
        focusable?.focus();
      }
    },
    [items.length],
  );

  /**
   * Toggle dropdown menu for a specific item.
   * Fires onTabChange when opening a menu (for telemetry purposes).
   */
  const toggleMenu = useCallback(
    (index: number) => {
      setMenuOpenState(prev => {
        const wasOpen = prev[index] ?? false;
        const willOpen = !wasOpen;

        if (willOpen && onTabChange) {
          onTabChange(items[index], index);
        }

        return {
          ...prev,
          [index]: willOpen,
        };
      });
    },
    [items, onTabChange],
  );

  /**
   * Close dropdown menu for a specific item
   */
  const closeMenu = useCallback((index: number) => {
    setMenuOpenState(prev => ({
      ...prev,
      [index]: false,
    }));
  }, []);

  const isMenuOpen = (index: number) => menuOpenState[index] ?? false;

  /**
   * Get props for a tab item (for accessibility and keyboard nav).
   *
   * Uses the navigation pattern: all links are naturally tabbable (no roving tabindex).
   * Arrow-key navigation is provided as a progressive enhancement.
   */
  const getItemProps = useCallback(
    (index: number) => {
      const isActive = index === activeIndex;
      return {
        'aria-current': isActive ? ('page' as const) : undefined,
        tabIndex: undefined,
        onKeyDown: (e: React.KeyboardEvent) => handleKeyDown(e, index),
      };
    },
    [activeIndex, handleKeyDown],
  );

  const setItemRef = (index: number) => (el: HTMLElement | null) => {
    itemRefs.current[index] = el;
  };

  return {
    activeIndex,
    toggleMenu,
    closeMenu,
    isMenuOpen,
    getItemProps,
    setItemRef,
    indicatorRef,
    itemRefs,
    listRef,
  };
}
